import type {
  Group,
  GroupMember,
  GroupMembersFilters,
  GroupMemberFilters,
  GetMembersParams,
} from "~/types/group.types";
import type {
  GROUP_MEMBER_ROLE,
} from '~/enums/group.enums';
import { B2BTrackingEvents } from "~/enums/tracking/b2b.enums";

const { user } = useUserStore();
const { dispatchTrackingEvent } = useTracking();

export default function useGroup () {
  const loading = ref<{
    group: boolean;
    groupMembers: boolean;
    groupMember: boolean;
  }>({
    group: false,
    groupMembers: false,
    groupMember: false,
  });

  const fetchError = ref(null as any | null);

  /*
   * fetch group data
   */
  const fetchGroup = async (): Promise<Group | undefined> => {
    return await useErrorHandling(async () => {
      if (!user.value?.group?.groupUuid) {
        throw new Error('Group ID is required to get group.');
      }

      loading.value.group = true;

      try {
        const url = useApiUrl(`/groups/${user.value.group.groupUuid}`);
        const response = await $fetch<{
          data: Group;
        }>(url, {
          method: 'GET',
        });

        if (!response.data) {
          throw new Error('Invalid response from server.');
        }

        return response.data;
      } catch (error) {
        fetchError.value = error;
        throw error;
      } finally {
        loading.value.group = false;
      }
    }, 'Fetching group failed');
  };

  /*
   * fetch group member's data
   */
  const fetchGroupMembers = async (
    filters: GroupMembersFilters,
    searchQuery: string,
    sortBy: string | undefined,
    sortDirection: 'desc' | 'asc' | undefined,
    rowsPerPage: number | undefined,
    page: number | undefined,
  ): Promise<{data: Array<GroupMember>; totalRecords: number} | undefined> => {
    return await useErrorHandling(async () => {
      if (!user.value?.group?.groupUuid) {
        throw new Error("Group ID is required to get group members.");
      }

      loading.value.groupMembers = true;

      const params: GetMembersParams = {
        per_page: rowsPerPage,
        page,
      };
      if (filters.role.value) {
        params.role = filters.role.value;
      }
      if (filters.status.value) {
        params.status = filters.status.value;
      }
      if (searchQuery?.length) {
        params['search'] = searchQuery;
      }
      if (sortBy) {
        params['sort_by'] = sortBy;
        params['sort_direction'] = sortDirection;
      }

      try {
        const url = useApiUrl(`/groups/${user.value.group.groupUuid}/members`);
        const response = await $fetch<{
          data: Array<GroupMember>;
          meta: {
            total: number;
            perPage: number;
            currentPage: number;
            lastPage: number;
          };
        }>(url, {
          method: 'GET',
          params,
        });

        if (!response.data) {
          throw new Error('Invalid response from server.');
        }

        return {
          data: response.data,
          totalRecords: response.meta.total,
        };
      } catch (error) {
        fetchError.value = error;
        throw error;
      } finally {
        loading.value.groupMembers = false;
      }
    }, 'Fetching group members failed');
  };

  /*
   * fetch group member data
   */
  const fetchGroupMember = async (
    memberId: string,
    filters: GroupMemberFilters,
    searchQuery: string,
    sortBy: string | undefined,
    sortDirection: 'desc' | 'asc' | undefined,
    rowsPerPage: number | undefined,
    page: number | undefined,
  ): Promise<{data: GroupMember; totalRecords: number} | undefined> => {
    return await useErrorHandling(async () => {
      if (!user.value?.group?.groupUuid) {
        throw new Error("Group ID is required to get group member.");
      }

      if (!memberId) {
        throw new Error("Member ID is required to get group member.");
      }

      loading.value.groupMember = true;

      interface GetMemberParams {
        status?: string | null;
        type?: string | null;
        search?: string;
        sort_by?: string | undefined;
        sort_direction?: 'desc' | 'asc' | undefined;
        per_page?: number | undefined;
        page?: number | undefined;
      }
      const params: GetMemberParams = {
        status: filters.status.value,
        type: filters.type.value,
        per_page: rowsPerPage,
        page,
      };
      if (searchQuery?.length) {
        params['search'] = searchQuery;
      }
      if (sortBy) {
        params['sort_by'] = sortBy;
        params['sort_direction'] = sortDirection;
      }

      try {
        const url = useApiUrl(`/groups/${user.value.group.groupUuid}/members/${memberId}`);
        const response = await $fetch<{
          data: GroupMember;
        }>(url, {
          method: 'GET',
          params,
        });

        if (!response.data) {
          throw new Error('Invalid response from server.');
        }

        return {
          data: response.data,
          totalRecords: response.data.pagination.total,
        };
      } catch (error) {
        fetchError.value = error;
        throw error;
      } finally {
        loading.value.groupMember = false;
      }
    }, 'Fetching group member failed');
  };

  const deactivateMembers = async (
    memberIds: Array<string>,
  ) => {
    if (!user.value?.group?.groupUuid) {
      throw new Error("Group ID is required to deactivate group member(s).");
    }

    try {
      const url = useApiUrl(`/groups/${user.value.group.groupUuid}/members/deactivate`);
      await useAuthenticatedFetch(url, {
        method: 'DELETE',
        credentials: 'include',
        body: {
          members: memberIds,
        },
      });
    } catch (error) {
      fetchError.value = error;
      throw error;
    }
  };

  /*
   * request group reports
   */
  const requestReports = async (
    reports: Array<'feedback' | 'activity'>,
  ) => {
    return await useErrorHandling(async () => {
      if (!user.value?.group?.groupUuid) {
        throw new Error("Group ID is required to request group reports.");
      }

      try {
        dispatchTrackingEvent(B2BTrackingEvents.ReportDownload, {});

        const url = useApiUrl(`/groups/${user.value.group.groupUuid}/reports`);
        await useAuthenticatedFetch(url, {
          method: 'POST',
          credentials: 'include',
          body: {
            reports,
          },
        });
      } catch (error) {
        fetchError.value = error;
        throw error;
      }
    }, 'Requesting reports failed');
  };

  /*
   * invite members to group
   */
  const inviteMembers = async (
    emails: Array<string>,
  ) => {
    if (!user.value?.group?.groupUuid) {
      throw new Error("Group ID is required to invite members to the group.");
    }

    const url = useApiUrl(`/groups/${user.value.group.groupUuid}/members/invite`);
    await useAuthenticatedFetch(url, {
      method: 'POST',
      credentials: 'include',
      body: {
        emails,
      },
    });
  };

  /*
   * change member's role
   * give OR revoke manager's access
   */
  const changeMemberRole = async (
    memberId: string,
    role: GROUP_MEMBER_ROLE.MANAGER | GROUP_MEMBER_ROLE.MEMBER,
  ) => {
    return await useErrorHandling(async () => {
      if (!user.value?.group?.groupUuid) {
        throw new Error("Group ID is required to change member's role.");
      }

      if (!memberId) {
        throw new Error("Member ID is required to change member's role.");
      }

      try {
        const url = useApiUrl(`/groups/${user.value.group.groupUuid}/members/${memberId}`);
        await useAuthenticatedFetch(url, {
          method: 'PUT',
          credentials: 'include',
          body: {
            b2bGroupUuid: user.value.group.groupUuid,
            role,
          },
        });
      } catch (error) {
        fetchError.value = error;
        throw error;
      }
    }, "Changing member's role failed");
  };

  const acceptGroupInvitation = async (
    groupId: string,
    expires: string,
    token: string,
    user: string,
    signature: string,
  ) => {
    return await useErrorHandling(async () => {
      try {
        const url = useApiUrl(`/groups/${groupId}/invite/accept`);
        const response = await $fetch<{
          message: string;
          email?: string | undefined;
          token?: string | undefined;
        }>(url, {
          method: 'GET',
          params: {
            expires,
            token,
            user,
            signature,
          },
        });

        if (!response) {
          throw new Error('Invalid response from server.');
        }

        return response;
      } catch (error) {
        fetchError.value = error;
        throw error;
      }
    }, "Accepting group invitation failed");
  };

  return {
    fetchError,
    loading,

    fetchGroup,
    fetchGroupMembers,
    fetchGroupMember,

    deactivateMembers,
    requestReports,
    inviteMembers,
    changeMemberRole,
    acceptGroupInvitation,
  };
}
